import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Mail, Lock, User, Building, ArrowRight, Eye, EyeOff, 
  Building2, UserCircle, Check, DollarSign, Briefcase, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { cn } from '../lib/utils';

// Member type options
const MEMBER_TYPES = [
  {
    id: 'business',
    name: 'Business / Organization',
    description: 'Launch your AI Horse-Powered Systems with 30 days free',
    icon: Building2,
    color: 'cyan',
    benefits: [
      'Directory listing for your business',
      'Access 40+ Workspace departments',
      'Create your own affiliate program',
      'Earn from affiliate promotions'
    ]
  },
  {
    id: 'personal',
    name: 'Affiliate / Individual',
    description: 'Become an affiliate and earn promoting businesses',
    icon: UserCircle,
    color: 'amber',
    benefits: [
      'Earn commissions as an affiliate',
      'Promote businesses in directory',
      'Access Workspace services for projects',
      'Full publishing capabilities'
    ]
  }
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1 = choose type, 2 = fill form
  const [memberType, setMemberType] = useState(null);
  
  const plan = searchParams.get('plan');
  const billing = searchParams.get('billing') || 'monthly';
  const intent = searchParams.get('intent');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    member_type: '',
    plan: plan || 'activation'
  });

  // If coming from pricing with a plan, auto-select business and go to form
  useEffect(() => {
    if (plan && plan !== 'get-listed') {
      // Coming from pricing page with a specific plan - they're a business
      setMemberType('business');
      setFormData(prev => ({ ...prev, member_type: 'business', plan: plan }));
      setStep(2);
    } else if (intent === 'business') {
      // Coming back from pricing, go straight to business form
      setMemberType('business');
      setFormData(prev => ({ ...prev, member_type: 'business' }));
      setStep(2);
    }
  }, [plan, intent]);

  const handleTypeSelect = (type) => {
    setMemberType(type);
    setFormData({ ...formData, member_type: type });
    
    // Business/Organization → redirect to pricing page to choose a plan
    if (type === 'business') {
      navigate('/pricing');
      return;
    }
    
    // Personal/Individual → continue to create free account
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (memberType === 'business' && !formData.company_name) {
      toast.error('Company name is required for business accounts');
      return;
    }
    
    setLoading(true);
    
    try {
      await register({
        ...formData,
        member_type: memberType,
        plan: plan
      });
      toast.success('Account created! Welcome aboard.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const goBackToTypeSelection = () => {
    setStep(1);
    setMemberType(null);
    // Clear URL params when going back
    navigate('/register', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-20 w-72 h-72 border border-white/20 rounded-full" />
          <div className="absolute bottom-32 left-16 w-40 h-40 border border-white/20 rounded-full" />
          <div className="absolute top-1/3 right-1/4 w-24 h-24 border border-white/10 rounded-full" />
        </div>
        
        <div className="text-center max-w-md relative z-10">
          <div className="mb-8">
            <img 
              src="/images/ai-horse-logo.png" 
              alt="AI Horse-Powered" 
              className="w-56 h-56 mx-auto mb-6 object-contain drop-shadow-[0_0_40px_rgba(34,211,238,0.3)]"
            />
          </div>
          
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            {step === 1 ? 'Choose Your Account Type' : 'Complete Your Registration'}
          </h2>
          <p className="text-gray-400">
            {step === 1 
              ? 'Select how you want to use the AI Horse-Powered platform'
              : memberType === 'business'
                ? 'Create your business account and start your free trial'
                : 'Join as an affiliate and start earning commissions'}
          </p>
          
          {/* Platform Features */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
              7 Platform Systems
            </span>
            <span className="px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-medium">
              AI Workforce
            </span>
            <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-xs font-medium">
              30 Days Free
            </span>
          </div>
          
          <div className="mt-8 grid grid-cols-3 gap-4">
            {['40+ Departments', '100 Credits', 'Affiliate Program'].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="glass rounded-xl p-4 text-center"
              >
                <span className="text-sm text-gray-300">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Logo size="lg" showText={true} />
          </Link>

          {/* Step 1: Member Type Selection */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                How will you use AI Horse-Powered?
              </h1>
              <p className="text-gray-400 mb-6">Choose your member type to get started</p>
              
              <div className="space-y-4">
                {MEMBER_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <motion.button
                      key={type.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleTypeSelect(type.id)}
                      className={cn(
                        "w-full p-5 rounded-xl border-2 text-left transition-all",
                        "hover:border-opacity-100",
                        type.color === 'cyan' 
                          ? "border-cyan-500/30 hover:border-cyan-500 bg-cyan-500/5 hover:bg-cyan-500/10"
                          : "border-amber-500/30 hover:border-amber-500 bg-amber-500/5 hover:bg-amber-500/10"
                      )}
                      data-testid={`member-type-${type.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center",
                          type.color === 'cyan' ? "bg-cyan-500/20" : "bg-amber-500/20"
                        )}>
                          <Icon className={cn(
                            "w-6 h-6",
                            type.color === 'cyan' ? "text-cyan-400" : "text-amber-400"
                          )} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-white text-lg">{type.name}</h3>
                          <p className="text-gray-400 text-sm mb-3">{type.description}</p>
                          <div className="space-y-1">
                            {type.benefits.map((benefit, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-gray-300">
                                <Check className={cn(
                                  "w-3 h-3",
                                  type.color === 'cyan' ? "text-cyan-400" : "text-amber-400"
                                )} />
                                {benefit}
                              </div>
                            ))}
                          </div>
                        </div>
                        <ArrowRight className={cn(
                          "w-5 h-5 mt-1",
                          type.color === 'cyan' ? "text-cyan-400" : "text-amber-400"
                        )} />
                      </div>
                    </motion.button>
                  );
                })}
              </div>
              
              <div className="mt-8 text-center">
                <p className="text-gray-400">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            </motion.div>
          )}

          {/* Step 2: Registration Form */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Back Button */}
              <button
                onClick={goBackToTypeSelection}
                className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Change account type</span>
              </button>
              
              {/* Selected Type Badge */}
              <div className={cn(
                "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium mb-4",
                memberType === 'business' 
                  ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30"
                  : "bg-amber-500/10 text-amber-400 border border-amber-500/30"
              )}>
                {memberType === 'business' ? (
                  <><Building2 className="w-4 h-4" /> Business Account</>
                ) : (
                  <><UserCircle className="w-4 h-4" /> Affiliate Account</>
                )}
              </div>

              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                Create your account
              </h1>
              <p className="text-gray-400 mb-6">
                {memberType === 'business' 
                  ? 'Set up your business listing and get started'
                  : 'Join as an affiliate and start earning'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      className="pl-10 input-dark"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                {/* Company Name - Required for Business, Optional for Personal */}
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-gray-300">
                    {memberType === 'business' ? 'Business / Organization Name' : 'Company Name (Optional)'}
                    {memberType === 'business' && <span className="text-red-400 ml-1">*</span>}
                  </Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="company"
                      type="text"
                      placeholder={memberType === 'business' ? 'Your Business Name' : 'Your Company Inc. (optional)'}
                      className="pl-10 input-dark"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required={memberType === 'business'}
                      data-testid="register-company-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      className="pl-10 input-dark"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      className="pl-10 pr-10 input-dark"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Minimum 6 characters</p>
                </div>

                {/* Plan Info Box - Show for business accounts */}
                {memberType === 'business' && (
                  <div className="p-4 rounded-xl border bg-primary/10 border-primary/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-primary">Partner Plan</span>
                      <span className="text-white font-bold">30 Days Free</span>
                    </div>
                    <p className="text-xs text-gray-400">
                      30-day free trial • All 7 platform systems • $120K Co-Op Funds • AI Workforce
                    </p>
                  </div>
                )}

                {/* Free account notice for affiliate accounts */}
                {memberType === 'personal' && (
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-amber-400 font-semibold">Free Affiliate Account</span>
                      <span className="text-white font-bold">$0</span>
                    </div>
                    <p className="text-xs text-gray-400">
                      Earn commissions • Promote businesses • Access publishing tools
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className={cn(
                    "w-full py-6 text-lg font-bold",
                    memberType === 'business'
                      ? "bg-cyan-500 hover:bg-cyan-600 text-black"
                      : "bg-amber-500 hover:bg-amber-600 text-black"
                  )}
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {memberType === 'business' ? 'Start Free Trial' : 'Create Free Account'}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
